import React, { useEffect, useMemo, useState } from 'react';
import { reportsAPI } from '../services/api';
import ReportTemplate from '../components/reports/ReportTemplate';
import './Reports.css';

const buildTenantKey = (tenant) => tenant?.id || tenant?.tenant_id || tenant?.tenantId || tenant?.name || tenant?.tenant_name || 'unassigned';
const buildTenantName = (tenant) => tenant?.name || tenant?.tenant_name || tenant?.tenant || tenant?.id || 'Unassigned Tenant';

const groupReportsByTenant = (reports) => reports.reduce((acc, report) => {
  const tenantKey = buildTenantKey(report) || buildTenantKey(report?.tenant) || 'unassigned';
  const tenantName = buildTenantName(report?.tenant || report);
  if (!acc[tenantKey]) {
    acc[tenantKey] = { tenantKey, tenantName, reports: [] };
  }
  acc[tenantKey].reports.push(report);
  return acc;
}, {});

const deriveTenants = (reports) => {
  const grouped = groupReportsByTenant(reports);
  return Object.values(grouped).map((entry) => ({ id: entry.tenantKey, name: entry.tenantName }));
};

function Reports() {
  const [tenants, setTenants] = useState([]);
  const [reportsByTenant, setReportsByTenant] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedReport, setGeneratedReport] = useState(null);
  const [branding, setBranding] = useState({
    mspName: '',
    mspLogo: null,
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    coBrandName: '',
    coBrandLogo: null,
    coBrandContact: ''
  });
  const [error, setError] = useState('');

  useEffect(() => {
    loadReports();
  }, []);

  const loadReports = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await reportsAPI.list();
      const data = response.data || {};
      const reports = Array.isArray(data) ? data : data.reports || [];
      const tenantList = data.tenants || deriveTenants(reports);

      setTenants(tenantList.map((tenant) => ({
        id: tenant.id || tenant.tenant_id || tenant.name,
        name: tenant.name || tenant.tenant_name || tenant.id || 'Tenant'
      })));
      setReportsByTenant(groupReportsByTenant(reports));
    } catch (loadError) {
      console.error('Error loading reports:', loadError);
      setError('Unable to load reports. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTenant = (tenant) => {
    setSelectedTenant(tenant);
    setGeneratedReport(null);
  };

  const handleBrandingChange = (field) => (event) => {
    const value = event.target.type === 'file' ? event.target.files[0] : event.target.value;
    setBranding((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const generatedTimestamp = useMemo(() => {
    if (!generatedReport?.generated_at) return null;
    return new Date(generatedReport.generated_at).toLocaleString();
  }, [generatedReport]);

  const canGenerate = selectedTenant && branding.contactEmail && branding.contactName;

  const handleGenerateReport = async (event) => {
    event.preventDefault();
    if (!selectedTenant) return;
    setIsGenerating(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('tenant_id', selectedTenant.id);
      formData.append('tenant_name', selectedTenant.name);
      formData.append('msp_name', branding.mspName);
      formData.append('contact_name', branding.contactName);
      formData.append('contact_email', branding.contactEmail);
      formData.append('contact_phone', branding.contactPhone);
      if (branding.mspLogo) {
        formData.append('msp_logo', branding.mspLogo);
      }
      if (branding.coBrandName) {
        formData.append('co_brand_name', branding.coBrandName);
      }
      if (branding.coBrandLogo) {
        formData.append('co_brand_logo', branding.coBrandLogo);
      }
      if (branding.coBrandContact) {
        formData.append('co_brand_contact', branding.coBrandContact);
      }

      const response = await reportsAPI.generate(formData);
      setGeneratedReport(response.data);
      await loadReports();
    } catch (generateError) {
      console.error('Error generating report:', generateError);
      setError('Unable to generate report. Please check the inputs and try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="reports-page">
      <header className="reports-header">
        <div>
          <h1>Reports Center</h1>
          <p>Generate branded security reports and track tenant readiness in one place.</p>
        </div>
      </header>

      <div className="reports-grid">
        <section className="reports-card">
          <div className="reports-card__header">
            <h2>Tenant Reports</h2>
            <p>Review existing reports by tenant and launch new report builds.</p>
          </div>
          {loading ? (
            <div className="spinner" />
          ) : (
            <div className="reports-tenant-list">
              {error && <div className="reports-error">{error}</div>}
              {tenants.length === 0 && (
                <p className="reports-empty">No tenants found yet. Start by generating your first report.</p>
              )}
              {tenants.map((tenant) => {
                const tenantReports = reportsByTenant[tenant.id]?.reports || [];
                return (
                  <div className="tenant-card" key={tenant.id}>
                    <div className="tenant-card__header">
                      <div>
                        <h3>{tenant.name}</h3>
                        <span>{tenantReports.length} reports</span>
                      </div>
                      <button
                        className="btn btn-primary"
                        type="button"
                        onClick={() => handleSelectTenant(tenant)}
                      >
                        Generate Report
                      </button>
                    </div>
                    <div className="tenant-card__body">
                      {tenantReports.length === 0 ? (
                        <p className="reports-empty">No reports generated for this tenant yet.</p>
                      ) : (
                        <ul>
                          {tenantReports.slice(0, 3).map((report) => (
                            <li key={report.id || report.created_at}>
                              <span>{report.title || 'Security Report'}</span>
                              <span>{report.status || 'Completed'}</span>
                              <span>{report.created_at ? new Date(report.created_at).toLocaleDateString() : 'Recent'}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <section className="reports-card">
          <div className="reports-card__header">
            <h2>Generate Report</h2>
            <p>Brand each report with MSP details and optional co-branding.</p>
          </div>
          {!selectedTenant ? (
            <p className="reports-empty">Select a tenant to configure and generate a report.</p>
          ) : (
            <form className="reports-form" onSubmit={handleGenerateReport}>
              <div className="reports-form__section">
                <h3>Tenant</h3>
                <div className="reports-form__highlight">
                  <strong>{selectedTenant.name}</strong>
                  <span>Tenant ID: {selectedTenant.id}</span>
                </div>
              </div>

              <div className="reports-form__section">
                <h3>MSP Branding</h3>
                <label>
                  MSP Name
                  <input
                    type="text"
                    value={branding.mspName}
                    onChange={handleBrandingChange('mspName')}
                    placeholder="PassiveGuard Managed Services"
                  />
                </label>
                <label>
                  MSP Logo
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleBrandingChange('mspLogo')}
                  />
                </label>
              </div>

              <div className="reports-form__section">
                <h3>Contact Info</h3>
                <label>
                  Contact Name
                  <input
                    type="text"
                    value={branding.contactName}
                    onChange={handleBrandingChange('contactName')}
                    placeholder="Security Operations Center"
                    required
                  />
                </label>
                <label>
                  Contact Email
                  <input
                    type="email"
                    value={branding.contactEmail}
                    onChange={handleBrandingChange('contactEmail')}
                    placeholder="soc@yourmsp.com"
                    required
                  />
                </label>
                <label>
                  Contact Phone
                  <input
                    type="text"
                    value={branding.contactPhone}
                    onChange={handleBrandingChange('contactPhone')}
                    placeholder="+1 (555) 010-9920"
                  />
                </label>
              </div>

              <div className="reports-form__section">
                <h3>Optional Co-branding</h3>
                <label>
                  Co-brand Name
                  <input
                    type="text"
                    value={branding.coBrandName}
                    onChange={handleBrandingChange('coBrandName')}
                    placeholder="Partner Organization"
                  />
                </label>
                <label>
                  Co-brand Logo
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleBrandingChange('coBrandLogo')}
                  />
                </label>
                <label>
                  Co-brand Contact
                  <input
                    type="text"
                    value={branding.coBrandContact}
                    onChange={handleBrandingChange('coBrandContact')}
                    placeholder="partner@organization.com"
                  />
                </label>
              </div>

              <button className="btn btn-primary" type="submit" disabled={!canGenerate || isGenerating}>
                {isGenerating ? 'Generating...' : 'Generate Report'}
              </button>
            </form>
          )}
        </section>
      </div>

      <section className="reports-card report-preview">
        <div className="reports-card__header">
          <h2>Report Preview</h2>
          <p>Live layout showing the standard report sections for stakeholders.</p>
        </div>
        <ReportTemplate
          tenant={selectedTenant}
          report={generatedReport}
          branding={branding}
          generatedAt={generatedTimestamp}
        />
      </section>
    </div>
  );
}

export default Reports;
